import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../../../services/apiClient';
import { useProducts } from '../../../../features/products/hooks';
import { brandConfig } from '../../../../config/brands';
import styles from '../ExpressModeModal.module.css';

interface StepAsinSearchProps {
  asin: string;
  accountId: string;
  amazonAccounts: Array<{ id: string; seller_id: string; marketplace_id: string; status: string }>;
  productInfo: {
    asin: string;
    title: string;
    brand_name?: string;
    main_image_url?: string;
    price_amount?: number;
    price_currency?: string;
  } | null;
  isSearching: boolean;
  isRegistering: boolean;
  registered: boolean;
  searchError: string | null;
  onAsinChange: (asin: string) => void;
  onAccountChange: (accountId: string) => void;
  onSearch: () => void;
  onRegister: () => void;
  onSelectManualProduct: (productId: string, title: string, imageUrl?: string, amazon?: { asin: string }) => void;
  onClose: () => void;
}

export function StepAsinSearch({
  asin,
  accountId,
  amazonAccounts,
  productInfo,
  isSearching,
  isRegistering,
  registered,
  searchError,
  onAsinChange,
  onAccountChange,
  onSearch,
  onRegister,
  onSelectManualProduct,
  onClose,
}: StepAsinSearchProps) {
  const amazonEnabled = brandConfig.features.amazonIntegration;
  // productsManualFirst のブランド (take8) は登録済み商品タブを先頭 + 初期選択にする
  const manualFirst = brandConfig.features.productsManualFirst === true;
  const productShort = brandConfig.labels.productShort;
  const navigate = useNavigate();
  const [tab, setTab] = useState<'asin' | 'manual'>(amazonEnabled && !manualFirst ? 'asin' : 'manual');
  const [manualSearch, setManualSearch] = useState('');

  const handleAddProduct = (e: React.MouseEvent) => {
    e.preventDefault();
    onClose();
    navigate('/products/manual');
  };

  // Amazon商品を取得
  const { data: amazonData, isLoading: isLoadingAmazon, isError: isAmazonError, refetch: refetchAmazon } = useQuery({
    queryKey: ['products', 'amazon', 'express'],
    queryFn: async () => {
      const response = await apiClient.get('/amazon/products', { params: { limit: 100 } });
      return response.data;
    },
    enabled: amazonEnabled,
  });

  // マニュアル商品を取得
  const { data: manualData, isLoading: isLoadingManual, isError: isManualError, refetch: refetchManual } = useProducts({ limit: 100 });

  const isLoadingProducts = isLoadingAmazon || isLoadingManual;
  const isProductsError = isAmazonError && isManualError;

  const refetchProducts = () => {
    refetchAmazon();
    refetchManual();
  };

  // 両テーブルの商品を統合
  const filteredProducts = useMemo(() => {
    const amazonProducts = (amazonData?.products || []).map((p: { id: string; title?: string; brand_name?: string; asin?: string; main_image_url?: string }) => ({
      id: p.id,
      title: p.title || '',
      brand_name: p.brand_name,
      sku: p.asin,
      main_image_url: p.main_image_url,
      source: 'amazon' as const,
    }));
    const manualProducts = (manualData?.products || []).map(p => ({
      id: p.id,
      title: p.title || '',
      brand_name: p.brand_name,
      sku: p.sku,
      main_image_url: p.main_image_url,
      source: p.source,
    }));
    const all = [...amazonProducts, ...manualProducts];

    if (!manualSearch.trim()) return all;
    const q = manualSearch.toLowerCase();
    return all.filter(p =>
      p.title?.toLowerCase().includes(q) ||
      p.brand_name?.toLowerCase().includes(q) ||
      p.sku?.toLowerCase().includes(q)
    );
  }, [amazonData, manualData, manualSearch]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && asin.trim() && !isSearching) {
      onSearch();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onAsinChange(e.target.value);
  };

  return (
    <div className={styles.stepContent}>
      <h2 className={styles.stepTitle}>{productShort}を選択</h2>

      {amazonEnabled && (
        <div className={styles.sourceTabRow}>
          {(() => {
            const asinTabButton = (
              <button
                key="asin"
                type="button"
                className={`${styles.sourceTab} ${tab === 'asin' ? styles.sourceTabActive : ''}`}
                onClick={() => setTab('asin')}
              >
                Amazon商品検索
              </button>
            );
            const manualTabButton = (
              <button
                key="manual"
                type="button"
                className={`${styles.sourceTab} ${tab === 'manual' ? styles.sourceTabActive : ''}`}
                onClick={() => setTab('manual')}
              >
                登録済み{productShort}
              </button>
            );
            return manualFirst ? [manualTabButton, asinTabButton] : [asinTabButton, manualTabButton];
          })()}
        </div>
      )}

      {amazonEnabled && tab === 'asin' && (
        <>
          {amazonAccounts.length > 0 && (
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Amazonアカウント</label>
              <select
                className={styles.formSelect}
                value={accountId}
                onChange={(e) => onAccountChange(e.target.value)}
              >
                <option value="">アカウントを選択</option>
                {amazonAccounts.map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {acc.seller_id} ({acc.marketplace_id})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Amazon URL / ASIN</label>
            <div className={styles.searchInputRow}>
              <input
                type="text"
                className={styles.formInput}
                value={asin}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="例: https://www.amazon.co.jp/dp/B0XXXXXXXXX または ASIN"
                disabled={isSearching}
              />
              <button
                type="button"
                className={styles.searchButton}
                onClick={onSearch}
                disabled={!asin.trim() || isSearching}
              >
                {isSearching ? (
                  <div className={styles.spinnerSmall} />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {searchError && (
            <div className={styles.errorMessage}>{searchError}</div>
          )}

          {productInfo && (
            <div className={styles.productCard}>
              {productInfo.main_image_url && (
                <img
                  src={productInfo.main_image_url}
                  alt={productInfo.title}
                  className={styles.productImage}
                />
              )}
              <div className={styles.productDetails}>
                <h3 className={styles.productTitle}>{productInfo.title}</h3>
                {productInfo.brand_name && (
                  <p className={styles.productBrand}>{productInfo.brand_name}</p>
                )}
                {productInfo.price_amount != null && (
                  <p className={styles.productPrice}>
                    {productInfo.price_currency === 'JPY' ? '¥' : productInfo.price_currency}
                    {productInfo.price_amount.toLocaleString()}
                  </p>
                )}
              </div>
              <button
                type="button"
                className={styles.registerButton}
                onClick={onRegister}
                disabled={isRegistering || registered}
              >
                {isRegistering ? (
                  <><div className={styles.spinnerSmall} /> 登録中...</>
                ) : registered ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    登録済み
                  </>
                ) : (
                  '登録して次へ'
                )}
              </button>
            </div>
          )}
        </>
      )}

      {tab === 'manual' && (
        <>
          <div className={styles.formGroup}>
            <div className={styles.searchInputRow}>
              <input
                type="text"
                className={styles.formInput}
                value={manualSearch}
                onChange={(e) => setManualSearch(e.target.value)}
                placeholder={`${productShort}名やブランド名で検索`}
              />
            </div>
            <div className={styles.manualAddRow}>
              <a
                href="/products/manual"
                className={styles.manualAddLink}
                onClick={handleAddProduct}
              >
                + 新しい{productShort}を追加
              </a>
            </div>
          </div>

          <div className={styles.manualProductList}>
            {isLoadingProducts ? (
              <div className={styles.manualProductLoading}>
                <div className={styles.spinnerSmall} />
                <span>{productShort}を読み込み中...</span>
              </div>
            ) : isProductsError ? (
              <div className={styles.manualProductError}>
                <p>{productShort}の取得に失敗しました</p>
                <button type="button" className={styles.retryButton} onClick={() => refetchProducts()}>
                  再取得
                </button>
              </div>
            ) : filteredProducts.length === 0 ? (
              <p className={styles.emptyMessage}>登録済み{productShort}がありません</p>
            ) : (
              filteredProducts.map(product => (
                <button
                  key={product.id}
                  type="button"
                  className={styles.manualProductItem}
                  onClick={() => onSelectManualProduct(
                    product.id,
                    product.title,
                    product.main_image_url,
                    product.source === 'amazon' && product.sku ? { asin: product.sku } : undefined,
                  )}
                >
                  {product.main_image_url ? (
                    <img
                      src={product.main_image_url}
                      alt={product.title}
                      className={styles.manualProductImage}
                    />
                  ) : (
                    <div className={styles.manualProductImagePlaceholder}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                    </div>
                  )}
                  <div className={styles.manualProductInfo}>
                    <span className={styles.manualProductTitle}>{product.title}</span>
                    {product.brand_name && (
                      <span className={styles.manualProductBrand}>{product.brand_name}</span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
